import { useTranslation } from 'react-i18next';
import { TopUpCoverNote } from 'features/credits/components/top-up-cover-note';
import {
  useCreditSpend,
  useCreditSummary,
  useTopUpCoversUsage,
} from 'features/credits/hooks/use-credits';
import { useFreeAccess } from 'features/subscription/hooks/use-seats';
import { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { WindowUsage } from 'features/usage/services/usage-service';
import { UsageCreditCard as CreditCard } from './usage-credit-card';
import { UsageMonthCard } from './usage-month-card';
import { formatCredits, UsageUnlimitedNotice } from './usage-credits';
import { UsagePlanCard } from './usage-plan-card';
import { UsageTodayCard } from './usage-today-card';

// Hidden for now; set to true to show the "Your plan" card again.
const SHOW_PLAN_CARD = false;

/** A person's daily and monthly plan credits, plus the organization's shared top-up balance. */
export function UsagePersonalCredits({
  userId,
  isOwnUsage,
  meter,
  windows,
}: {
  /** Whose shared-credit spend to show: the signed-in person, or the member an admin opened. */
  userId: string;
  isOwnUsage: boolean;
  /** This person's AI-credits meter for the current billing period. */
  meter?: SubscriptionUsageMeter;
  windows: WindowUsage[];
}) {
  const { t } = useTranslation();
  const { isFreeAccess } = useFreeAccess();
  const { data: summary } = useCreditSummary();
  const topUpCovers = useTopUpCoversUsage();

  const dailyWindow = windows.find((window) => window.type === 'daily') ?? windows[0];
  const monthPercent = meter && meter.included > 0 ? (meter.used / meter.included) * 100 : 0;
  const isAnyLimitFull = monthPercent >= 100 || windows.some((window) => window.percent >= 100);
  const showShared = !!summary?.enabled && summary.hasAccess !== false;
  // Without the id the server would answer with the whole organization's spend.
  const { data: spend } = useCreditSpend(showShared && !!userId, userId);

  if (isFreeAccess) return <UsageUnlimitedNotice />;
  const hasCreditCards = !!dailyWindow || !!meter || showShared;

  return (
    <div className="flex flex-col gap-6">
      {SHOW_PLAN_CARD && (
        <UsagePlanCard meter={meter} limitName={dailyWindow?.name || 'Monthly limit'} />
      )}
      {hasCreditCards && (
        <div className="space-y-3">
          <div className="grid gap-x-4 gap-y-6 md:grid-cols-3">
            <UsageTodayCard windows={windows} />

            {meter && <UsageMonthCard meter={meter} />}

            {showShared && (
              <CreditCard
                title={t('USAGE_SHARED_CREDITS', { defaultValue: 'Shared credits' })}
                value={formatCredits(summary.balanceCredits)}
                unit={t('USAGE_CREDITS_LEFT_LABEL', { defaultValue: 'credits left' })}
                footer={t('USAGE_SHARED_CREDITS_NOTE', {
                  defaultValue:
                    'Shared with your team. Used after your plan runs out. Never expire.',
                })}
              >
                {spend && (
                  <p className="text-xs text-muted-foreground">
                    {isOwnUsage
                      ? t('USAGE_SHARED_CREDITS_YOU_USED', {
                          credits: formatCredits(spend.lifetimeSpentCredits),
                          defaultValue: "You've used {{credits}}",
                        })
                      : t('USAGE_SHARED_CREDITS_MEMBER_USED', {
                          credits: formatCredits(spend.lifetimeSpentCredits),
                          defaultValue: 'Used {{credits}}',
                        })}
                  </p>
                )}
              </CreditCard>
            )}
          </div>

          {topUpCovers && isAnyLimitFull && <TopUpCoverNote />}
        </div>
      )}
    </div>
  );
}
